import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { useGetList } from "react-admin";
import { determineUserReadableRole } from "../users/UserUtils";

// ------------------------------------------------------------------
// Regras de Usuário: permissões, perfil resultante no painel e
// proteções. Compartilhado pela lista, ficha e formulários.
// ------------------------------------------------------------------

// Permissões conhecidas (enum AppRoles do backend, app.roles.ts). O
// "efeito" descreve o que o MENU do painel mostra para cada perfil
// (AppMenus.tsx) — é o que dá para afirmar com segurança.
export const PERMISSIONS = [
  {
    id: "ADMIN_MANAGE_ANY_ANYTHING",
    name: "Administrador",
    effect: "Acesso a todo o painel",
  },
  {
    id: "USER_MANAGE_FINANCE",
    name: "Financeiro",
    effect: "Sócios, Transações e Iugu",
  },
  {
    id: "USER_VIEW_BASIC_CONTENT",
    name: "Comunicação",
    effect: "Gestão de Membros (Sócios)",
  },
  {
    id: "USER_VIEW_FULL_CONTENT",
    name: "Secretaria",
    effect: "Gestão de Membros (Sócios)",
  },
  {
    id: "USER_MANAGE_OWN_CONTENT",
    name: "Sócio",
    effect: "Área do sócio: cadastro, cobranças, assinaturas e contato",
  },
  {
    id: "PROJECT_MANAGER_MANAGE_ANY_CONTENT",
    name: "Gerente de projeto",
    effect: "Não muda o menu do painel",
  },
];

export const permissionInfo = (code: string) =>
  PERMISSIONS.find((p) => p.id === code);

// Perfil que o painel aplica a quem tem a regra — mesma função usada no
// login/menu (determineUserReadableRole), que escolhe o de maior acesso
const PROFILES: Record<
  string,
  { label: string; color: "primary" | "success" | "info" | "warning" | "default" }
> = {
  admin: { label: "Administrador", color: "primary" },
  finance: { label: "Financeiro", color: "success" },
  comms: { label: "Comunicação", color: "warning" },
  secretary: { label: "Secretaria", color: "info" },
  user: { label: "Sócio", color: "default" },
  unknown: { label: "Sem perfil", color: "default" },
};

export const PROFILE_PRIORITY_NOTE =
  "Com mais de uma permissão, o painel aplica a de maior acesso, nesta ordem: Administrador, Financeiro, Comunicação, Secretaria, Sócio.";

export const asPermissionList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : [];

export const ProfileChip = ({ permissions }: { permissions: unknown }) => {
  const profile =
    PROFILES[determineUserReadableRole(asPermissionList(permissions))] ||
    PROFILES.unknown;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={profile.color}
      label={profile.label}
    />
  );
};

// Etiquetas das permissões; código técnico no tooltip. Código fora da
// lista conhecida aparece como está (nada é escondido)
export const PermissionChips = ({ permissions }: { permissions: unknown }) => {
  const list = asPermissionList(permissions);
  if (list.length === 0) return <span>—</span>;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
      {list.map((code) => {
        const info = permissionInfo(code);
        return (
          <Tooltip key={code} title={info ? `${info.effect} — ${code}` : code}>
            <Chip size="small" label={info ? info.name : code} />
          </Tooltip>
        );
      })}
    </span>
  );
};

// Nomes que o servidor usa literalmente — renomear quebra o sistema:
// "User" no cadastro e na aprovação de sócios (auth.service,
// users.service); os demais liberam a lista de Sócios para a equipe
// (users.service, checkAdminRole). Mesmo motivo do aviso em roleLabel.ts.
export const RESERVED_ROLE_NAMES = [
  "User",
  "Admin",
  "Secretário(a)",
  "Comunicação",
  "Financeiro",
  "Manager",
];

export const isReservedRole = (name?: string | null) =>
  !!name && RESERVED_ROLE_NAMES.includes(name);

// Quantos usuários têm a regra (consulta só o total da lista de Sócios
// filtrada pela regra — perPage 1)
export const useRoleUserCount = (roleId?: string) => {
  const { total, isLoading } = useGetList(
    "users/crud",
    {
      filter: { roleId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: Boolean(roleId) }
  );
  return { count: typeof total === "number" ? total : undefined, isLoading };
};

// Motivo para não permitir excluir a regra (ou undefined se pode)
export const roleDeleteBlockReason = (
  name: string | undefined,
  count: number | undefined
) => {
  if (isReservedRole(name)) {
    return "Regra usada pelo sistema — não pode ser excluída";
  }
  if (count === undefined) return "Conferindo usuários da regra…";
  if (count > 0) {
    return `${count.toLocaleString("pt-BR")} usuário(s) com esta regra — mude a regra deles antes de excluir`;
  }
  return undefined;
};
